import { z } from "zod";
import { bilingualTitleBodyFields } from "../i18n";

export const contentStatusSchema = z.enum(["draft", "pending_review", "approved", "rejected", "published", "withdrawn"]);
export const applicationDocumentTypeSchema = z.enum(["user_agreement", "privacy_policy", "membership_agreement", "about_us", "community_guidelines"]);
/** App legal/product documents are first-class content, alongside campaign material. */
export const contentTypeSchema = z.enum(["user_agreement", "privacy_policy", "membership_agreement", "about_us", "community_guidelines", "article", "image", "video", "text"]);
export const contentIdSchema = z.string().uuid();
export const contentItemSchema = z.object({
  id: contentIdSchema, title: z.string(), titleUg: z.string(), type: contentTypeSchema, status: contentStatusSchema,
  appId: z.string().nullable(), authorId: z.string(), currentVersion: z.number().int().positive(), lockVersion: z.number().int().positive(),
  createdAt: z.string(), updatedAt: z.string(),
  publishedVersion: z.number().int().positive().nullable().default(null), publishedAt: z.string().nullable().default(null),
});
export const contentListQuerySchema = z.object({
  keyword: z.string().trim().max(100).default(""), status: contentStatusSchema.optional(), type: contentTypeSchema.optional(), appId: z.string().max(128).optional(),
  page: z.coerce.number().int().min(1).max(100000).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export const contentListResponseSchema = z.object({ items: z.array(contentItemSchema), total: z.number().int().nonnegative(), page: z.number().int(), pageSize: z.number().int() });
export const contentRevisionSchema = z.object({
  id: z.string(), contentId: z.string(), version: z.number().int(),
  title: z.string().optional(), titleUg: z.string().optional(),
  body: z.string(), bodyUg: z.string().optional(),
  createdBy: z.string(), createdAt: z.string(),
});
export const contentReviewEntrySchema = z.object({ id: z.string(), action: z.enum(["create", "update", "submit", "approve", "reject", "publish", "withdraw"]), reason: z.string().nullable(), reviewer: z.string(), version: z.number().int(), createdAt: z.string(), requestId: z.string().nullable().default(null) });
export const contentDetailSchema = contentItemSchema.extend({
  revisions: z.array(contentRevisionSchema), reviews: z.array(contentReviewEntrySchema),
  reviewReason: z.string().nullable(), reviewedAt: z.string().nullable(), reviewedBy: z.string().nullable(),
});
export const contentCreateSchema = z.object({
  ...bilingualTitleBodyFields(10000),
  type: contentTypeSchema,
  appId: z.string().trim().min(1, "请选择关联应用").max(128),
}).strict();
export const contentUpdateSchema = z.object({
  ...bilingualTitleBodyFields(10000),
  expectedVersion: z.number().int().positive(),
}).strict();
/** Deliberately excludes drafts, actors, internal IDs, and review/audit metadata. */
export const publishedApplicationDocumentSchema = z.object({
  appId: z.string(), type: applicationDocumentTypeSchema,
  revisionId: z.uuid().nullable().default(null),
  ...bilingualTitleBodyFields(10000),
  version: z.number().int().positive(), publishedAt: z.string(),
});
export const contentSubmitSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
export const contentReviewSchema = z.object({
  expectedVersion: z.number().int().positive(), approved: z.boolean(), reason: z.string().trim().max(1000).default(""),
}).strict().refine((value) => value.approved || value.reason.length > 0, { path: ["reason"], message: "驳回时必须填写原因" });
export const contentPublishSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
export type ContentItem = z.infer<typeof contentItemSchema>;
export type ContentListQuery = z.infer<typeof contentListQuerySchema>;
export type ContentRevision = z.infer<typeof contentRevisionSchema>;
export type ContentDetail = z.infer<typeof contentDetailSchema>;
export type ContentListResponse = z.infer<typeof contentListResponseSchema>;
export type ContentCreate = z.infer<typeof contentCreateSchema>;
export type ContentUpdate = z.infer<typeof contentUpdateSchema>;
export type PublishedApplicationDocument = z.infer<typeof publishedApplicationDocumentSchema>;
export type ContentPublish = z.infer<typeof contentPublishSchema>;
export const consentDocumentTypeSchema = z.enum(["user_agreement", "privacy_policy", "membership_agreement"]);
export const contentConsentSnapshotSchema = z.object({
  type: consentDocumentTypeSchema,
  currentVersion: z.number().int().positive().nullable(),
  publishedAt: z.string().nullable(),
  agreedVersion: z.number().int().positive().nullable(),
  agreedAt: z.string().nullable(),
  current: z.boolean(),
  currentRevisionId: z.uuid().nullable().default(null),
  agreedRevisionId: z.uuid().nullable().default(null),
  receiptId: z.string().nullable().default(null),
});
export const recordContentConsentInputSchema = z.object({ version: z.number().int().positive(), revisionId: z.uuid() }).strict();
export const adminContentConsentSchema = z.object({
  id: z.string(), appId: z.string(), appName: z.string(), appCode: z.string(),
  userId: z.string(), userPublicId: z.string(), userName: z.string(),
  type: consentDocumentTypeSchema, agreedVersion: z.number().int().positive(),
  currentVersion: z.number().int().positive().nullable(), current: z.boolean(),
  agreedRevisionId: z.uuid().nullable().default(null), currentRevisionId: z.uuid().nullable().default(null),
  agreedAt: z.string(), updatedAt: z.string(),
});
export const adminContentConsentListQuerySchema = z.object({
  keyword: z.string().trim().max(100).default(""),
  type: z.union([consentDocumentTypeSchema, z.literal("all")]).default("all"),
  appId: z.string().max(128).default(""),
  match: z.enum(["all", "current", "stale", "unavailable"]).default("all"),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export const adminContentConsentListResultSchema = z.object({
  items: z.array(adminContentConsentSchema),
  total: z.number().int().nonnegative(),
  summary: z.object({
    total: z.number().int().nonnegative(),
    current: z.number().int().nonnegative(),
    stale: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative().default(0),
  }),
});
export type ConsentDocumentType = z.infer<typeof consentDocumentTypeSchema>;
export type ContentConsentSnapshot = z.infer<typeof contentConsentSnapshotSchema>;
export type RecordContentConsentInput = z.infer<typeof recordContentConsentInputSchema>;
export type AdminContentConsent = z.infer<typeof adminContentConsentSchema>;
export type AdminContentConsentListQuery = z.infer<typeof adminContentConsentListQuerySchema>;
export type AdminContentConsentListResult = z.infer<typeof adminContentConsentListResultSchema>;

export const announcementStatusSchema = z.enum(["draft", "published", "withdrawn"]);
export const announcementWindowSchema = z.enum(["all", "current", "scheduled", "expired", "unavailable"]);
const optionalInstant = z.preprocess((value) => value === "" || value === undefined ? null : value, z.string().datetime().nullable());
const announcementWindowFields = {
  startsAt: optionalInstant,
  endsAt: optionalInstant,
};
function refineAnnouncementWindow(value: { startsAt: string | null; endsAt: string | null }, context: z.RefinementCtx) {
  if (value.startsAt && value.endsAt && Date.parse(value.startsAt) >= Date.parse(value.endsAt)) {
    context.addIssue({ code: "custom", path: ["endsAt"], message: "结束时间必须晚于开始时间" });
  }
}
export const publishedAnnouncementSchema = z.object({
  id: z.string(), appId: z.string(), ...bilingualTitleBodyFields(4000),
  startsAt: z.string().nullable(), endsAt: z.string().nullable(),
  publishedAt: z.string(), version: z.number().int().positive(),
});
export const publishedAnnouncementListSchema = z.object({ items: z.array(publishedAnnouncementSchema) });
export const adminAnnouncementSchema = z.object({
  id: z.string(), appId: z.string(), appName: z.string(), appCode: z.string(),
  ...bilingualTitleBodyFields(4000), status: announcementStatusSchema,
  startsAt: z.string().nullable(), endsAt: z.string().nullable(),
  window: z.enum(["current", "scheduled", "expired", "none", "unavailable"]),
  publishedAt: z.string().nullable(), version: z.number().int().positive(),
  createdAt: z.string(), updatedAt: z.string(),
});
export const adminAnnouncementListQuerySchema = z.object({
  keyword: z.string().trim().max(100).default(""),
  status: z.union([announcementStatusSchema, z.literal("all")]).default("all"),
  window: announcementWindowSchema.default("all"),
  appId: z.string().max(128).default(""),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export const adminAnnouncementListResultSchema = z.object({
  items: z.array(adminAnnouncementSchema),
  total: z.number().int().nonnegative(),
  summary: z.object({
    total: z.number().int().nonnegative(),
    current: z.number().int().nonnegative(),
    scheduled: z.number().int().nonnegative(),
    expired: z.number().int().nonnegative(),
    draft: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative().default(0),
  }),
});
export const adminAnnouncementCreateSchema = z.object({
  appId: z.string().trim().min(1, "请选择关联应用").max(128),
  ...bilingualTitleBodyFields(4000),
  ...announcementWindowFields,
}).strict().superRefine(refineAnnouncementWindow);
export const adminAnnouncementUpdateSchema = z.object({
  ...bilingualTitleBodyFields(4000),
  ...announcementWindowFields,
  expectedVersion: z.number().int().positive(),
}).strict().superRefine(refineAnnouncementWindow);
export const adminAnnouncementPublishSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
export type AnnouncementStatus = z.infer<typeof announcementStatusSchema>;
export type PublishedAnnouncement = z.infer<typeof publishedAnnouncementSchema>;
export type AdminAnnouncement = z.infer<typeof adminAnnouncementSchema>;
export type AdminAnnouncementListQuery = z.infer<typeof adminAnnouncementListQuerySchema>;
export type AdminAnnouncementListResult = z.infer<typeof adminAnnouncementListResultSchema>;
export type AdminAnnouncementCreateInput = z.infer<typeof adminAnnouncementCreateSchema>;
export type AdminAnnouncementUpdateInput = z.infer<typeof adminAnnouncementUpdateSchema>;
export type AdminAnnouncementPublishInput = z.infer<typeof adminAnnouncementPublishSchema>;

export const contentConsentHistoryQuerySchema = z.object({page:z.coerce.number().int().min(1).max(100000).default(1),pageSize:z.coerce.number().int().min(1).max(100).default(20)}).strict();
export const contentConsentReceiptSchema = z.object({id:z.uuid(),version:z.number().int().positive(),revisionId:z.uuid().nullable(),agreedAt:z.string(),origin:z.enum(['live','legacy']),title:z.string().nullable(),titleUg:z.string().nullable(),body:z.string().nullable(),bodyUg:z.string().nullable()});
export const contentConsentHistorySchema = z.object({items:z.array(contentConsentReceiptSchema),total:z.number().int().nonnegative()});
export type ContentConsentHistory = z.infer<typeof contentConsentHistorySchema>;
